/**
 * Lightweight client-side catalog-search request timing (issue #272 / aps-boot #272).
 *
 * Measures request-dispatch -> response-resolved wall time with the Performance API
 * and logs a structured `SEARCH_TIMING_FE` line, mirroring the backend `SEARCH_TIMING`
 * log so the two can be correlated. Browser-only: on the server (SSR prefetch) it is a
 * no-op so it never touches RSC render timing.
 *
 * `kind` distinguishes the catalog request shapes the user asked to separate:
 *  - "search"  -> keyword search (useProductSearch / searchProducts)
 *  - "hotsale" -> channel-only browse (useHotSaleInfinite / listProductHotSale)
 *
 * Observation only — does not change request behavior. Re-throws on error after
 * logging the failed timing so callers/react-query see the original error untouched.
 */
export interface SearchTimingMeta {
  kind: "search" | "hotsale";
  channel?: string;
  /** keyword length only — never the keyword itself (avoid logging user PII). */
  keywordLen?: number;
  pageNo?: number;
}

function isBrowser(): boolean {
  return typeof window !== "undefined" && typeof performance !== "undefined";
}

export async function withSearchTiming<T>(
  meta: SearchTimingMeta,
  fn: () => Promise<T>,
): Promise<T> {
  if (!isBrowser()) {
    return fn();
  }
  const start = performance.now();
  try {
    const result = await fn();
    logTiming(meta, start, "ok");
    return result;
  } catch (err) {
    logTiming(meta, start, "error");
    throw err;
  }
}

function logTiming(meta: SearchTimingMeta, startMs: number, outcome: string): void {
  const totalMs = Math.round(performance.now() - startMs);
  // Single structured line, grep-friendly and aligned with backend SEARCH_TIMING.
  // eslint-disable-next-line no-console -- intentional perf telemetry, see issue #272
  console.info(
    `SEARCH_TIMING_FE kind=${meta.kind} outcome=${outcome} channel=${meta.channel ?? "-"} ` +
      `page=${meta.pageNo ?? "-"} keywordLen=${meta.keywordLen ?? 0} total=${totalMs}ms`,
  );
}
